import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { Input, Area, ImageInput, VideoInput, MultiImageField, LegibilityControls } from "./fields";
import { FrameEditor } from "./FrameEditor";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";

const uid = () => `act-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;

// Visual slide manager for the "Sur le terrain" (Activités) carousel.
// Reuses the shared FrameEditor ("Mode Transformer global") — no new cropping tool.
export const AdminActivities = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(null);
  const v = value || {};
  const items = v.items || [];
  const setMeta = (k, val) => onChange({ ...v, [k]: val });
  const setItems = (arr) => onChange({ ...v, items: arr });
  const patch = (i, p) => setItems(items.map((it, x) => (x === i ? { ...it, ...p } : it)));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; setItems(c); };
  const add = () => {
    setItems([...items, { key: uid(), title: "Nouvelle activité", description: "", image: "", images: [], videoUrl: "", enabled: true }]);
    setOpen(items.length);
  };

  const thumbOf = (it) => it.image || (Array.isArray(it.images) ? it.images.map((x) => (x && typeof x === "object" ? x.url : x)).find(Boolean) : "") || "";

  // Fiche dédiée : formulaire complet d'UNE slide (modification isolée)
  const renderForm = (i) => {
    const it = items[i];
    if (!it) return null;
    return (
      <>
        <div className="grid grid-cols-1 gap-3">
          <Input label="Titre" value={it.title} onChange={(val) => patch(i, { title: val })} />
          <Area label="Texte / description" value={it.description} onChange={(val) => patch(i, { description: val })} aiContext="la description d'une activité de l'association" />
        </div>

        <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Image de la slide</span>
          <ImageInput label="Image principale (ou 1re photo du carrousel)" value={it.image} onChange={(val) => patch(i, { image: val })} testid={`activity-image-${i}`} />
          <MultiImageField label="Photos supplémentaires (plusieurs = carrousel dans le même cadre)" value={it.images || []} onChange={(val) => patch(i, { images: val })} />
          <VideoInput label="Vidéo (URL — prioritaire si renseignée)" value={it.videoUrl} onChange={(val) => patch(i, { videoUrl: val })} />
        </div>

        <FrameEditor label="Cadrage de l'image (Mode Transformer)" src={thumbOf(it)} value={it.imageFrame} onChange={(val) => patch(i, { imageFrame: val })}
          aspect="landscape" testid={`activity-frame-${i}`} hint="Glissez l'image (souris ou doigt), zoomez et positionnez le sujet. Le cadre reste fixe." />

        <LegibilityControls value={it.effects} onChange={(val) => patch(i, { effects: val })} testid={`activity-legibility-${i}`} />
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-activities-editor">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">
        Gérez ici toutes les slides de la section « Sur le terrain » : image, titre, texte, ordre, visibilité et cadrage — sans toucher au code.
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Input label="Sur-titre" value={v.eyebrow} onChange={(val) => setMeta("eyebrow", val)} />
        <Area label="Titre" value={v.heading} onChange={(val) => setMeta("heading", val)} />
        <Area label="Intro" value={v.intro} onChange={(val) => setMeta("intro", val)} />
      </div>

      <div className="flex items-center justify-between border-t border-white/10 pt-5">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Slides ({items.length})</span>
        <button onClick={add} data-testid="admin-add-activity" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter une slide</button>
      </div>

      {open !== null && items[open] ? (
        <FicheOverlay testid="activity-fiche" title={items[open].title} subtitle={items[open].enabled === false ? "Slide · Masquée" : "Slide · Visible"} onClose={() => setOpen(null)}>
          {renderForm(open)}
        </FicheOverlay>
      ) : (
        <>
          {items.length === 0 && <p className="text-secondary2 text-sm">Aucune slide. Cliquez sur « Ajouter une slide ».</p>}
          <CardGrid testid="activities-cards">
            {items.map((it, i) => {
              const disabled = it.enabled === false;
              return (
                <ItemCard key={it.key || i} testid={`admin-activity-item-${i}`}
                  image={thumbOf(it)} frame={it.imageFrame}
                  title={it.title || `Slide ${i + 1}`} subtitle={disabled ? "Masquée" : `Visible${it.videoUrl ? " · Vidéo" : ""}`}
                  dimmed={disabled}
                  onOpen={() => setOpen(i)}
                  actions={
                    <>
                      <button onClick={() => patch(i, { enabled: disabled })} data-testid={`activity-toggle-${i}`} title={disabled ? "Afficher" : "Masquer"}
                        className={`p-2 rounded border border-white/10 ${disabled ? "text-white/30" : "text-electric"} hover:border-electric`}>{disabled ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                      <button onClick={() => move(i, -1)} data-testid={`activity-up-${i}`} title="Monter" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
                      <button onClick={() => move(i, 1)} data-testid={`activity-down-${i}`} title="Descendre" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
                      <button onClick={() => setItems(items.filter((_, x) => x !== i))} data-testid={`activity-delete-${i}`} title="Supprimer" className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                    </>
                  } />
              );
            })}
          </CardGrid>
        </>
      )}
    </div>
  );
};
